/**
 * frontend/src/pages/valuation/_FinancialsYearTable.tsx
 *
 * VAL-UPLOAD-U1B-1 — one column per fiscal year, one row per figure. Read-only.
 * A figure that was not read renders empty (never "0" or "$0"); a real 0 renders 0.
 * Clicking a figure shows where it came from, straight from the stored provenance.
 */
import { useState } from 'react'
import type { CellProvenance, FinancialsRow } from '@/api/valuationFinancials'

// The one COGS label on every screen (owner-approved 10-21 20:53).
export const COGS_LABEL = 'Cost of goods sold'

export const FIELDS: Array<{ key: string; label: string }> = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'cogs', label: COGS_LABEL },
  { key: 'operating_expenses', label: 'Operating expenses' },
  { key: 'depreciation', label: 'Depreciation' },
  { key: 'amortization', label: 'Amortization' },
  { key: 'interest', label: 'Interest' },
  { key: 'tax', label: 'Tax' },
]

/** Display text for one figure: empty only when nothing was read. */
function show(value: unknown): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return ''
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

interface Props {
  rows: FinancialsRow[]
  provenance: Record<string, unknown>
}

export function FinancialsYearTable({ rows, provenance }: Props) {
  const [picked, setPicked] = useState<string | null>(null)
  const years = [...rows].map(r => r.year).sort((a, b) => b - a)
  const byYear = new Map(rows.map(r => [r.year, r]))
  const src = picked ? (provenance[picked] as CellProvenance | undefined) : undefined

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-pal-line bg-pal-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-right text-pal-ink-soft">
              <th scope="col" className="p-2 text-left" />
              {years.map(y => (
                <th key={y} scope="col" className="p-2 font-medium">FY{y}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FIELDS.map(f => (
              <tr key={f.key} className="border-b border-gray-100 last:border-0">
                <th scope="row" className="p-2 text-left font-normal text-pal-ink-body">{f.label}</th>
                {years.map(y => {
                  const text = show(byYear.get(y)?.[f.key])
                  const key = `${y}.${f.key}`
                  return (
                    <td key={y} className="p-0 text-right">
                      {text === '' ? (
                        <span data-testid={`cell-${y}-${f.key}`} className="block p-2" />
                      ) : (
                        <button
                          type="button"
                          data-testid={`cell-${y}-${f.key}`}
                          aria-pressed={picked === key}
                          onClick={() => setPicked(picked === key ? null : key)}
                          className="block w-full p-2 text-right tabular-nums hover:bg-pal-info-bg"
                        >
                          {text}
                        </button>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {picked && (
        <div data-testid="cell-source" className="rounded-lg border border-pal-line bg-pal-surface-subtle p-3 text-sm text-gray-800">
          {src ? (
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
              <dt className="text-pal-ink-muted">File</dt><dd>{src.filename}</dd>
              <dt className="text-pal-ink-muted">Line</dt><dd>{src.source_label}</dd>
              <dt className="text-pal-ink-muted">Location</dt><dd>{src.locator}</dd>
              {src.note ? (<><dt className="text-pal-ink-muted">Note</dt><dd>{src.note}</dd></>) : null}
            </dl>
          ) : (
            <p>No source is recorded for this figure.</p>
          )}
        </div>
      )}
    </div>
  )
}
